import { useForm } from "react-hook-form";
import { BaseFormControlledModal } from "../../../../../../common/components/base_form_controlled_modal"
import { DefaultFormAutocomplete } from "../../../../../../common/components/default_form_autocomplete";
import { DefaultFormSelectInput } from "../../../../../../common/components/default_form_select_input";
import { DefaultFormDatePickerInput } from "../../../../../../common/components/default_form_date_picker_input";
import { InspecaoObraOcorrencia } from "../../../../domain/models/inspecao_obra_ocorrencia";
import { useEffect, useMemo } from "react";
import { InspecaoObraOcorrenciaForm, InspecaoObraOcorrenciaSchema } from "../../../schemas/ObraItensChecklistArea_verificacao/inspecao_obra_ocorrencia";
import { zodResolver } from "@hookform/resolvers/zod";
import { InspecaoObraItem } from "../../../../domain/models/inspecao_obra_item";

import FormContainer from "../../../../../../common/components/form_container";
import DefaultFormInput from "../../../../../../common/components/default_form_input";
import dayjs from "dayjs";

/**
 * OcorrenciaModal
 */

type Props = {
  open: boolean;
  saving: boolean;
  loading: boolean;
  fillOcorrenciaData: boolean;
  inspecoesObraItens: InspecaoObraItem[];
  selectedInspecaoObraItem: InspecaoObraItem | null;
  selectedOcorrencia: InspecaoObraOcorrencia | null;
  onClose: () => void;
  onSubmit: (form: InspecaoObraOcorrenciaForm) => void;
};

export const OcorrenciaModal = ({
  open,
  saving,
  loading,
  fillOcorrenciaData,
  inspecoesObraItens,
  selectedInspecaoObraItem,
  selectedOcorrencia,
  onClose,
  onSubmit,
}: Props) => {
  const hookForm = useForm<InspecaoObraOcorrenciaForm>({
    mode: "onSubmit",
    reValidateMode: "onSubmit",
    resolver: zodResolver(InspecaoObraOcorrenciaSchema),
  });

  const inspecaoOptions = useMemo(() => {
    return [
      {
        id: 1,
        label: '1ª Inspeção'
      },
      {
        id: 2,
        label: '2ª Inspeção'
      },
    ];
  }, [
  ]);

  useEffect(() => {
    if (open) {
      if (selectedOcorrencia) {
        hookForm.setValue('idItemInspecao', selectedOcorrencia.idInspecaoObraItens);
        hookForm.setValue('descricao', selectedOcorrencia.descricao ?? '');
        hookForm.setValue('tratativa', selectedOcorrencia.tratativa ?? '');
        hookForm.setValue('dataDescricao', selectedOcorrencia.data ? dayjs(selectedOcorrencia.data).toDate() : null);
        hookForm.setValue('dataTratativa', selectedOcorrencia.dataTratativa ? dayjs(selectedOcorrencia.dataTratativa).toDate() : null);
        hookForm.setValue('inspecao', selectedOcorrencia.inspecaoValue);
      } else {
        hookForm.reset({
          idItemInspecao: selectedInspecaoObraItem && selectedInspecaoObraItem.idInspecaoObraItens
            ? selectedInspecaoObraItem.idInspecaoObraItens
            : null,
          inspecao: fillOcorrenciaData ? 1 : null,
          dataDescricao: fillOcorrenciaData ? dayjs(new Date()).startOf('day').utc().toDate() : null,
        });
      }
    }
  }, [
    open,
    fillOcorrenciaData,
    selectedOcorrencia,
    selectedInspecaoObraItem,
    hookForm,
  ]);

  return (
    <BaseFormControlledModal
      open={open}
      saving={saving}
      loading={loading}
      title='Ocorrência'
      width="45rem"
      onClose={onClose}
      onSubmit={hookForm.handleSubmit(onSubmit)}>
      <FormContainer title="Informações" arrangement="fullRow" border={false} padding="small">
        <>
          <DefaultFormAutocomplete
            name='idItemInspecao'
            label='Item de Inspeção'
            options={inspecoesObraItens}
            getOptionKey={x => x.idInspecaoObraItens}
            getOptionLabel={x => x.descricao ?? ''}
            reactHookForm={hookForm}
          />

          <DefaultFormSelectInput
            name='inspecao'
            label='Inspeção'
            options={inspecaoOptions}
            reactHookForm={hookForm}
          />
        </>
      </FormContainer>

      <FormContainer title="Descrição" arrangement="fullRow" border={false} padding="small">
        <>
          <DefaultFormDatePickerInput
            name='dataDescricao'
            label='Data'
            reactHookForm={hookForm}
          />

          <DefaultFormInput
            name='descricao'
            label='Descrição'
            required={true}
            multiline={true}
            rows={8}
            reactHookForm={hookForm}
          />
        </>
      </FormContainer>

      <FormContainer title="Tratativa" arrangement="fullRow" border={false} padding="small">
        <>
          <DefaultFormDatePickerInput
            name='dataTratativa'
            label='Data'
            reactHookForm={hookForm}
          />

          <DefaultFormInput
            name='tratativa'
            label='Tratativa'
            multiline={true}
            rows={8}
            reactHookForm={hookForm}
          />
        </>
      </FormContainer>
    </BaseFormControlledModal>
  );
}